import { useCallback, useEffect, useState } from 'react';
import { appToast } from '@utils/appToast';
import { handleShowToastError } from '@utils/helpers';
import { inventorySessionApi } from '@services/inventorySessionApi';
import { nxIntegrationApi } from '@services/nxIntegrationApi';
import { TInventorySessionItem } from '@models/asset';
import {
  NX_INTEGRATION_CONSTANT,
  TImportLog,
  TInventoryYearMonthOption,
  formatImportDatetime,
  formatYearMonth,
  formatYearMonthDisplayShifted,
  isFebruaryOrAugust,
  toImportStatus,
} from '../constant';

const { MESSAGE } = NX_INTEGRATION_CONSTANT;
const SUCCESS_TOAST_DURATION = 2; // seconds — MSG-PC010-S01

function useNXIntegrationPage() {
  const [activeTab, setActiveTab] = useState<string>('ledger');
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>('');
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(true);

  const [importLog, setImportLog] = useState<TImportLog>({
    importYearMonth: null,
    importDatetime: null,
    recordCount: null,
    status: null,
  });
  const [yearMonthOptions, setYearMonthOptions] = useState<
    TInventoryYearMonthOption[]
  >([]);

  // ── Load Tab2 dropdown from inventory sessions (2月/8月 data, shown as 3月/9月) ─
  // The 除却 CSV must come from the Feb/Aug inventory session, but the customer
  // wants it labeled as the following month (Mar/Sep) since that's when it's used.
  const loadDropdownOptions = useCallback(
    (sessions: TInventorySessionItem[]) => {
      const options: TInventoryYearMonthOption[] = sessions
        .filter((s) => isFebruaryOrAugust(s.inventoryYearMonth))
        .map((s) => ({
          value: s.inventoryYearMonth, // actual session month, e.g. "202502"
          label: formatYearMonthDisplayShifted(s.inventoryYearMonth), // e.g. "2025/03"
        }));

      setYearMonthOptions(options);
      if (options.length > 0) {
        setSelectedYearMonth(options[0].value);
      }
    },
    []
  );

  // ── Load Tab1 import log: newest inventory session → older until hit ───────
  // Inventory sessions (棚卸) are separate from NX batch import jobs (CSVJobs), so
  // walk sessions newest-first (whatever session is latest, even a future-dated
  // one like 202609) rather than assuming "now" is the newest possible session.
  const loadLatestImportLog = useCallback(
    async (sessions: TInventorySessionItem[]) => {
      for (const session of sessions) {
        try {
          const logRes = await nxIntegrationApi.getImportLog(
            session.inventoryYearMonth
          );
          // csv-job-by-session returns raw CsvJobDetailResponse → check .success directly
          if (logRes.success) {
            setImportLog({
              importYearMonth: formatYearMonth(logRes.inventoryYearMonth),
              importDatetime: formatImportDatetime(logRes.finishedAt),
              recordCount: logRes.recordCount,
              status: toImportStatus(logRes.status),
            });
            return; // found — stop
          }
        } catch {
          // 404 → no batch for this session, try older
        }
      }
      // No import found for any known session → fields remain null (UI shows "—")
    },
    []
  );

  const loadInitialData = useCallback(async () => {
    setLoadingPage(true);
    try {
      const sessionRes = await inventorySessionApi.getSessions();
      const sessions =
        sessionRes.ok && Array.isArray(sessionRes.data)
          ? [...sessionRes.data].sort((a, b) =>
              b.inventoryYearMonth.localeCompare(a.inventoryYearMonth)
            )
          : [];

      loadDropdownOptions(sessions);
      await loadLatestImportLog(sessions);
    } catch (error) {
      handleShowToastError(error);
    } finally {
      setLoadingPage(false);
    }
  }, [loadDropdownOptions, loadLatestImportLog]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleChangeTab = (key: string) => {
    setActiveTab(key);
  };

  const handleChangeYearMonth = (value: string) => {
    setSelectedYearMonth(value);
  };

  const handleClickCsvOutput = () => {
    setShowExportDialog(true);
  };

  const handleCancelExportDialog = () => {
    setShowExportDialog(false);
  };

  // Customer-facing display month (Mar/Sep) for the selected Feb/Aug session
  const selectedYearMonthDisplay =
    yearMonthOptions.find((opt) => opt.value === selectedYearMonth)?.label ??
    '';

  const handleConfirmExport = async () => {
    setIsExporting(true);
    try {
      // selectedYearMonth already holds the actual session month (e.g. "202502")
      const blob = await nxIntegrationApi.exportDispose({
        inventoryYearMonth: selectedYearMonth,
      });
      setShowExportDialog(false);

      // JSON blob → rowCount = 0, no records to export
      if (blob.type.includes('application/json')) {
        appToast.warning({ message: MESSAGE.I01 });
        return;
      }

      // CSV blob → trigger browser download
      const url = URL.createObjectURL(blob);
      const fileName = `除却取込データ（${selectedYearMonthDisplay}）`;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      appToast.success({
        message: MESSAGE.S01,
        duration: SUCCESS_TOAST_DURATION,
      });
    } catch (error: any) {
      appToast.error({ message: MESSAGE.E02 });
      handleShowToastError(error);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    state: {
      activeTab,
      importLog,
      yearMonthOptions,
      selectedYearMonth,
      selectedYearMonthDisplay,
      showExportDialog,
      isExporting,
      loadingPage,
    },
    handler: {
      handleChangeTab,
      handleChangeYearMonth,
      handleClickCsvOutput,
      handleCancelExportDialog,
      handleConfirmExport,
    },
  };
}

export default useNXIntegrationPage;
